// Shared formatting + date helpers for the campaign console.
import { Campaign, CampaignTier, CampaignStatus, Recurrence } from "../../../api/maintenance/campaigns";

export const num = (v: number | null | undefined, d = 0) =>
  v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });
export const money = (v: number | null | undefined, d = 0) => (v == null ? "—" : `₹${num(v, d)}`);
export const ts = (s: string | null | undefined) =>
  s ? new Date(s.endsWith("Z") || s.includes("+") ? s : s + "Z").toLocaleString("en-IN", { hour12: false, dateStyle: "medium", timeStyle: "short" }) : "—";

const IST_OFFSET_MIN = 330;

/** Server timestamps are naive UTC. Render as an IST "YYYY-MM-DDTHH:MM" for datetime-local inputs. */
export const utcToIstInput = (s: string | null | undefined): string => {
  if (!s) return "";
  const d = new Date(s.endsWith("Z") ? s : s + "Z");
  const ist = new Date(d.getTime() + IST_OFFSET_MIN * 60000);
  return ist.toISOString().slice(0, 16);
};
/** datetime-local value typed as IST -> ISO with Z. */
export const istInputToUtcIso = (v: string): string => {
  if (!v) return "";
  const asUtc = new Date(v + ":00Z");
  return new Date(asUtc.getTime() - IST_OFFSET_MIN * 60000).toISOString().replace(/\.\d{3}Z$/, "Z");
};

export const hoursUntil = (iso: string): number => {
  const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
  return (d.getTime() - Date.now()) / 3600000;
};

export const statusPill = (s: CampaignStatus): string =>
  s === "live" ? "ok" : s === "scheduled" ? "warn" : s === "cancelled" ? "bad" : s === "draft" ? "info" : "muted";

export const tierPays = (t: CampaignTier) =>
  t.is_random ? `${t.min_percentage}–${t.max_percentage}%` : `flat ${t.fixed_percentage}%`;
export const tierBand = (t: CampaignTier) =>
  `₹${num(t.min_amount)}${t.max_amount == null ? " and up" : ` – ₹${num(t.max_amount)}`}`;

/** The plain-English restatement of a campaign's limits. */
export const readsAs = (c: Partial<Campaign>): string => {
  const parts: string[] = [];
  if (c.max_awards) parts.push(`First ${num(c.max_awards)} recharges`);
  if (c.budget_cap) parts.push(c.max_awards ? "while funds last" : "While funds last");
  const reset: Record<string, string> = { campaign: "during the offer", day: "a day", week: "a week", month: "a month" };
  const r = reset[c.per_user_reset || "campaign"];
  if (c.per_user_award_limit)
    parts.push(`up to ${c.per_user_award_limit === 1 ? "once" : c.per_user_award_limit === 2 ? "twice" : `${c.per_user_award_limit} times`} ${r} each`);
  if (c.per_user_cap) parts.push(`up to ${money(c.per_user_cap)} ${r} each`);
  if (c.max_cashback_cap) parts.push(`${money(c.max_cashback_cap)} max per reward`);
  if (!parts.length) return "No limits other than the dates.";
  const s = parts.join(", ");
  return s.charAt(0).toUpperCase() + s.slice(1) + ".";
};

/** The server normalises tags on write; do the same here so the operator sees
 *  what will actually be stored rather than what they typed. "Diwali 2026",
 *  "diwali-2026" and " DIWALI-2026 " are one tag — otherwise they are three and
 *  the grouping is worthless within a week. */
export const MAX_TAGS = 12;
export const normalizeTag = (raw: string): string =>
  raw.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-")
    .replace(/^-|-$/g, "").slice(0, 40);

export const normalizeTags = (raw: string[]): string[] => {
  const out: string[] = [];
  for (const t of raw) {
    const n = normalizeTag(t);
    if (n && !out.includes(n)) out.push(n);
  }
  return out.slice(0, MAX_TAGS);
};

const WEEKDAY = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** A recurrence in the words an operator would use. */
export const recurrenceLabel = (r: Recurrence | null | undefined): string => {
  if (!r) return "Runs continuously between its dates";
  if (r.type === "daily") return `Every day, ${r.from}–${r.to}`;
  if (r.type === "weekly") {
    const days = (r.days || []).slice().sort().map((d) => WEEKDAY[d] || d).join(", ");
    return `${days || "no days"}, ${r.from}–${r.to}`;
  }
  if (r.type === "monthly_first_n") return `First ${r.n} day${r.n === 1 ? "" : "s"} of each month`;
  return "Custom schedule";
};

/** Live but between run windows: on the books, not paying. */
export const isPaused = (c: Pick<Campaign, "status"> & { active_now?: boolean }): boolean =>
  c.status === "live" && c.active_now === false;

// Mirrors ALLOWED_IMAGE_TYPES / MAX_IMAGE_SIZE in the campaigns route. Checked
// here as well so a 3MB photo is refused while the operator is still looking at
// the file picker, rather than after the upload round trip.
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
export const MAX_IMAGE_KB = 1024;

/** Why a file cannot be campaign artwork, or null when it can. */
export const imageProblem = (file: File): string | null => {
  if (!IMAGE_TYPES.includes(file.type)) return `${file.type || "That file"} is not a push-safe image. Use PNG, JPEG or WebP.`;
  if (file.size > MAX_IMAGE_KB * 1024)
    return `${Math.round(file.size / 1024)}KB is too big for a push. FCM gives up on large images and the notification arrives with no picture at all. Keep it under ${MAX_IMAGE_KB}KB.`;
  return null;
};

export const errMsg = (e: any, fallback: string): string => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  return fallback;
};
